import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Shield, Lock, FileText, Trash2, Download, Clock,
  RefreshCw, CheckCircle, AlertTriangle, XCircle,
  User, Calendar, ChevronLeft, ChevronRight, Eye, Search
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const ComplianceCenter = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gdpr');
  const [requests, setRequests] = useState([]);
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [activeTab, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch admin logs which would include compliance-related actions
      const logsRes = await adminAPI.getLogs ? 
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logs?skip=${page * limit}&limit=${limit}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('vett_token')}` }
        }).then(r => r.json()) : 
        { logs: [] };
      
      // Filter for compliance-related logs
      const complianceTypes = ['export_user_data', 'delete_user', 'pause_account', 'gdpr_request', 'data_export'];
      setComplianceLogs(logsRes.logs?.filter(log => complianceTypes.includes(log.action_type)) || []);
      
      // Mock GDPR requests - in production this would come from a dedicated endpoint
      setRequests([]);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportUserData = async (userId) => {
    try {
      const res = await adminAPI.exportUserData(userId);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_data_${userId}_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('User data exported successfully');
    } catch (error) {
      toast.error('Failed to export user data');
    }
  };

  // Data Retention Policies
  const retentionPolicies = [
    { 
      dataType: 'User Account Data', 
      retention: '3 years after account deletion',
      basis: 'Legal obligation',
      status: 'active'
    },
    { 
      dataType: 'VETT Records', 
      retention: '7 years',
      basis: 'Legitimate interest (employment verification)',
      status: 'active'
    },
    { 
      dataType: 'Payment Information', 
      retention: '10 years',
      basis: 'Legal obligation (tax/accounting)',
      status: 'active'
    },
    { 
      dataType: 'Email Logs', 
      retention: '90 days',
      basis: 'Legitimate interest (deliverability)',
      status: 'active'
    },
    { 
      dataType: 'Session/Activity Logs', 
      retention: '30 days',
      basis: 'Security monitoring',
      status: 'active'
    },
    { 
      dataType: 'Analytics Data', 
      retention: '2 years (anonymized)',
      basis: 'Legitimate interest',
      status: 'active'
    }
  ];

  // EEOC/FCRA Compliance Info
  const complianceInfo = [
    {
      regulation: 'FCRA (Fair Credit Reporting Act)',
      status: 'Compliant',
      lastAudit: '2025-10-15',
      details: 'Employment verification follows FCRA guidelines. Users consent to data collection.',
      requirements: [
        'Written consent before background checks',
        'Pre-adverse action notice',
        'Adverse action notice with copy of report',
        'User dispute rights'
      ]
    },
    {
      regulation: 'EEOC Guidelines',
      status: 'Compliant',
      lastAudit: '2025-10-15',
      details: 'Platform does not discriminate based on protected characteristics.',
      requirements: [
        'No discriminatory job history requirements',
        'Equal access to verification services',
        'Reasonable accommodation support',
        'Anti-discrimination policy enforcement'
      ]
    },
    {
      regulation: 'GDPR (EU)',
      status: 'Compliant',
      lastAudit: '2025-11-01',
      details: 'Full GDPR compliance for EU users including data portability and deletion rights.',
      requirements: [
        'Lawful basis for processing',
        'Right to access',
        'Right to erasure',
        'Data portability',
        'Breach notification (72h)'
      ]
    },
    {
      regulation: 'CCPA (California)',
      status: 'Compliant',
      lastAudit: '2025-11-01',
      details: 'California users have full privacy rights including opt-out of data sale.',
      requirements: [
        'Right to know',
        'Right to delete',
        'Right to opt-out of sale',
        'Non-discrimination'
      ]
    }
  ];

  const refreshActions = (
    <Button
      onClick={fetchData}
      variant="outline"
      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
    >
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh
    </Button>
  );

  return (
    <AdminLayout title="Compliance Center" breadcrumbs={[{ label: 'Compliance' }]} actions={refreshActions}>
      <div data-testid="compliance-center">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5] mb-6">
            <TabsTrigger value="gdpr" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              GDPR/CCPA Requests
            </TabsTrigger>
            <TabsTrigger value="regulations" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              EEOC/FCRA Compliance
            </TabsTrigger>
            <TabsTrigger value="retention" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Data Retention
            </TabsTrigger>
            <TabsTrigger value="consent" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Cookie Consent
            </TabsTrigger>
          </TabsList>

          {/* GDPR/CCPA Requests */}
          <TabsContent value="gdpr" className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <span className="text-[#5B6472] text-sm">Total Requests</span>
                </div>
                <p className="text-2xl font-bold text-[#0A0F1E]">{requests.length}</p>
              </Card>
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-[#5B6472] text-sm">Pending</span>
                </div>
                <p className="text-2xl font-bold text-[#0A0F1E]">{requests.filter(r => r.status === 'pending').length}</p>
              </Card>
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-[#5B6472] text-sm">Completed</span>
                </div>
                <p className="text-2xl font-bold text-[#0A0F1E]">{requests.filter(r => r.status === 'completed').length}</p>
              </Card>
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span className="text-[#5B6472] text-sm">Avg. Response</span>
                </div>
                <p className="text-2xl font-bold text-[#0A0F1E]">2.3 days</p>
              </Card>
            </div>

            {/* Requests Table */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
                </div>
              ) : requests.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F8FF]/50">
                      <tr>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">User</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Type</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Requested</th>
                        <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {/* Request rows would go here */}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
                  <p className="text-lg">No Pending Requests</p>
                  <p className="text-sm">All GDPR/CCPA requests have been processed</p>
                </div>
              )}
            </Card>

            {/* Compliance Logs */}
            {complianceLogs.length > 0 && (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4">Recent Compliance Actions</h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {complianceLogs.map((log, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                      <div className="w-8 h-8 rounded-lg bg-[#F5F8FF] flex items-center justify-center">
                        {log.action_type === 'export_user_data' ? (
                          <Download className="w-4 h-4 text-blue-400" />
                        ) : log.action_type === 'delete_user' ? (
                          <Trash2 className="w-4 h-4 text-red-400" />
                        ) : (
                          <FileText className="w-4 h-4 text-purple-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#0A0F1E] text-sm">{log.action_type.replace(/_/g, ' ')}</p>
                        <p className="text-[#5B6472] text-xs">by {log.admin_email}</p>
                      </div>
                      <span className="text-[#5B6472] text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* EEOC/FCRA Compliance */}
          <TabsContent value="regulations" className="space-y-6">
            {complianceInfo.map((item, index) => (
              <Card key={index} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-blue-400" />
                    <div>
                      <h3 className="text-[#0A0F1E] font-semibold">{item.regulation}</h3>
                      <p className="text-[#5B6472] text-sm">Last audit: {item.lastAudit}</p>
                    </div>
                  </div>
                  <Badge className={
                    item.status === 'Compliant' ? 'bg-green-500/20 text-green-400 border-0' :
                    item.status === 'Pending Review' ? 'bg-amber-500/20 text-amber-400 border-0' :
                    'bg-red-500/20 text-red-400 border-0'
                  }>
                    {item.status}
                  </Badge>
                </div>
                
                <p className="text-[#5B6472] mb-4">{item.details}</p>
                
                <div className="grid md:grid-cols-2 gap-3">
                  {item.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded bg-[#FFFFFF]">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-[#0A0F1E] text-sm">{req}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Data Retention */}
          <TabsContent value="retention" className="space-y-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              <div className="p-6 border-b border-[#E8EDF5]">
                <h3 className="text-[#0A0F1E] font-semibold">Data Retention Policies</h3>
                <p className="text-[#5B6472] text-sm mt-1">
                  Our data retention policies comply with GDPR, CCPA, and industry standards.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F5F8FF]/50">
                    <tr>
                      <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Data Type</th>
                      <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Retention Period</th>
                      <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Legal Basis</th>
                      <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8EDF5]">
                    {retentionPolicies.map((policy, index) => (
                      <tr key={index} className="hover:bg-[#F5F8FF]/30">
                        <td className="p-4">
                          <span className="text-[#0A0F1E]">{policy.dataType}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-[#5B6472]">{policy.retention}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-[#5B6472]">{policy.basis}</span>
                        </td>
                        <td className="p-4">
                          <Badge className="bg-green-500/20 text-green-400 border-0">
                            {policy.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Cookie Consent */}
          <TabsContent value="consent" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Consent Stats */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4">Cookie Consent Statistics</h3>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#5B6472]">All Cookies Accepted</span>
                      <span className="text-green-400 font-semibold">68%</span>
                    </div>
                    <div className="h-2 bg-[#F5F8FF] rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '68%' }} />
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#5B6472]">Essential Only</span>
                      <span className="text-amber-400 font-semibold">24%</span>
                    </div>
                    <div className="h-2 bg-[#F5F8FF] rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: '24%' }} />
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#5B6472]">Custom Selection</span>
                      <span className="text-blue-400 font-semibold">8%</span>
                    </div>
                    <div className="h-2 bg-[#F5F8FF] rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '8%' }} />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Cookie Categories */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4">Cookie Categories</h3>
                <div className="space-y-3">
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-[#0A0F1E] font-medium">Essential</p>
                          <p className="text-[#5B6472] text-xs">Required for basic functionality</p>
                        </div>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400 border-0">Always On</Badge>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-[#0A0F1E] font-medium">Analytics</p>
                          <p className="text-[#5B6472] text-xs">Usage analytics and improvements</p>
                        </div>
                      </div>
                      <Badge className="bg-blue-500/20 text-blue-400 border-0">Optional</Badge>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-[#0A0F1E] font-medium">Personalization</p>
                          <p className="text-[#5B6472] text-xs">Remember preferences</p>
                        </div>
                      </div>
                      <Badge className="bg-purple-500/20 text-purple-400 border-0">Optional</Badge>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ComplianceCenter;
